// @spec SHL-STR-016

import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAppStore } from '@/stores/app-store';

export interface UseGlobalSearchReturn {
  /** Whether search dialog is open */
  isOpen: boolean;
  /** Open the search dialog */
  openSearch: () => void;
  /** Close the search dialog */
  closeSearch: () => void;
  /** Toggle search dialog */
  toggleSearch: () => void;
}

/**
 * Hook for managing the global search dialog.
 *
 * Registers the `Cmd/Ctrl + K` keyboard shortcut to open the search dialog
 * and provides state controls via the app store.
 *
 * @example
 * function Header() {
 *   const { openSearch } = useGlobalSearch();
 *
 *   return (
 *     <Button onClick={openSearch}>Search</Button>
 *   );
 * }
 */
export function useGlobalSearch(): UseGlobalSearchReturn {
  const isOpen = useAppStore.use.searchOpen();
  const setSearchOpen = useAppStore.use.setSearchOpen();
  const toggleSearchStore = useAppStore.use.toggleSearch();

  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, [setSearchOpen]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
  }, [setSearchOpen]);

  const toggleSearch = useCallback(() => {
    toggleSearchStore();
  }, [toggleSearchStore]);

  // Register Cmd/Ctrl + K keyboard shortcut
  useHotkeys(
    'mod+k',
    (event) => {
      event.preventDefault();
      toggleSearch();
    },
    {
      scopes: ['global'],
      enableOnFormTags: ['INPUT', 'TEXTAREA', 'SELECT'],
      enableOnContentEditable: false,
    }
  );

  return {
    isOpen,
    openSearch,
    closeSearch,
    toggleSearch,
  };
}
